/*
  Carrossel da section "Coleção da Estampa".

  O carrossel em si é CSS: o trilho é um flex com overflow-x + scroll-snap, e o
  swipe do mobile é o scroll nativo. Este arquivo existe só para o que o CSS não
  faz — mover o scroll ao clicar nas setas e manter o estado de início/fim delas.
  Sem JS (ou antes dele carregar) o conteúdo continua acessível por scroll: as
  setas nascem escondidas por `no-js-hidden`.
*/
if (!customElements.get('estampa-collection-carousel')) {
  class EstampaCollectionCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-ec-track]');

      if (!this.track || this.initialized) return;
      this.initialized = true;

      this.prevButton = this.querySelector('[data-ec-prev]');
      this.nextButton = this.querySelector('[data-ec-next]');

      this.onScroll = this.onScroll.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPrev = this.page.bind(this, -1);
      this.onNext = this.page.bind(this, 1);

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('keydown', this.onKeydown);

      if (this.prevButton) this.prevButton.addEventListener('click', this.onPrev);
      if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);

      // O trilho muda de largura no resize e quando as imagens dos cards
      // terminam de carregar — os dois mexem no estado das setas.
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onScroll);
        this.resizeObserver.observe(this.track);
      }

      this.update();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);

      if (this.track) {
        this.track.removeEventListener('scroll', this.onScroll);
        this.track.removeEventListener('keydown', this.onKeydown);
      }

      if (this.prevButton) this.prevButton.removeEventListener('click', this.onPrev);
      if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext);

      this.initialized = false;
    }

    // Um update por frame, no máximo: o scroll dispara muito mais que isso.
    onScroll() {
      if (this.frame) return;

      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    onKeydown(event) {
      switch (event.key) {
        case 'ArrowRight':
          event.preventDefault();
          this.page(1);
          break;
        case 'ArrowLeft':
          event.preventDefault();
          this.page(-1);
          break;
        case 'Home':
          event.preventDefault();
          this.scrollTo(0);
          break;
        case 'End':
          event.preventDefault();
          this.scrollTo(this.maxScroll);
          break;
        default:
          break;
      }
    }

    get maxScroll() {
      return this.track.scrollWidth - this.track.clientWidth;
    }

    get behavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth';
    }

    get isRtl() {
      return getComputedStyle(this.track).direction === 'rtl';
    }

    // Uma "página" é a largura visível do trilho, que é exatamente o conjunto
    // de cards visíveis — os slides preenchem 100% da largura por breakpoint.
    page(direction) {
      const amount = this.track.clientWidth * (this.isRtl ? -1 : 1);

      this.track.scrollBy({ left: direction * amount, behavior: this.behavior });
    }

    scrollTo(position) {
      this.track.scrollTo({
        left: this.isRtl ? -position : position,
        behavior: this.behavior
      });
    }

    update() {
      const max = this.maxScroll;
      const scrolls = max > 2;
      // Em RTL o scrollLeft é negativo; o valor absoluto serve aos dois sentidos.
      const position = Math.abs(this.track.scrollLeft);

      this.classList.toggle('is-static', !scrolls);

      // Sem overflow não há região rolável: o trilho sai da ordem de tabulação
      // para não virar uma parada de teclado sem função.
      this.track.setAttribute('tabindex', scrolls ? '0' : '-1');

      if (this.prevButton) this.prevButton.disabled = !scrolls || position <= 2;
      if (this.nextButton) this.nextButton.disabled = !scrolls || position >= max - 2;
    }
  }

  customElements.define('estampa-collection-carousel', EstampaCollectionCarousel);
}
